package one.healthium.bland.presentation.navigation.home

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LifecycleEventEffect
import com.arkivanov.decompose.extensions.compose.stack.Children
import com.arkivanov.decompose.extensions.compose.stack.animation.fade
import com.arkivanov.decompose.extensions.compose.stack.animation.stackAnimation
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import kotlinx.coroutines.launch
import loco.MAIN_NAVIGATION_CHAT_LABEL
import loco.MAIN_NAVIGATION_DAILY_LABEL
import loco.MAIN_NAVIGATION_INSIGHTS_LABEL
import loco.MAIN_NAVIGATION_ME_LABEL
import loco.MAIN_NAVIGATION_TODAY_LABEL
import one.healthium.bland.presentation.navigation.today.TodayContent
import one.healthium.content.navigation.insights.InsightsContent
import one.healthium.domain.app_visit.AppVisitCounter
import one.healthium.profile.navigation.ProfileMainContent
import one.healthium.tasks.navigation.DailyTasksContent
import one.healthium.ui.compose.components.AppNavigationBar
import one.healthium.ui.compose.components.AppNavigationBarItem
import one.healthium.ui.compose.components.LocalGlobalProgressIndicator
import one.healthium.ui.compose.components.NavigationBarText
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

@Composable
fun HomeContent(
    component: HomeComponent,
) {
    val appVisit = koinInject<AppVisitCounter>()
    val coroutineScope = rememberCoroutineScope()

    LifecycleEventEffect(
        event = Lifecycle.Event.ON_RESUME
    ) {
        coroutineScope.launch {
            appVisit.countAppVisit()
        }
    }

    Column(Modifier.fillMaxSize()) {
        Children(
            stack = component.stack,
            modifier = Modifier.weight(1f),
            animation = stackAnimation(fade())
        ) {
            when (val child = it.instance) {
                is HomeComponent.Child.TodayChild -> TodayContent(child.component)
                is HomeComponent.Child.DailyTasksChild -> DailyTasksContent(child.component)
                is HomeComponent.Child.InsightsChild -> InsightsContent(child.component)
                is HomeComponent.Child.ChatChild -> Box {}
                is HomeComponent.Child.ProfileChild -> ProfileMainContent(child.component)
            }
        }
        BottomNavigation(component)
    }
}

@Composable
private fun BottomNavigation(
    component: HomeComponent,
) {
    val stack by component.stack.subscribeAsState()
    val activeComponent = stack.active.instance
    val scope = rememberCoroutineScope()
    val globalProgressIndicator = LocalGlobalProgressIndicator.current

    AppNavigationBar(
        color = AppTheme.colors.primary,
    ) {
        // Today
        AppNavigationBarItem(
            modifier = Modifier.testTag("nav_today").weight(1f),
            icon = {
                NavigationBarText(
                    label = stringResource(loco.Res.string.MAIN_NAVIGATION_TODAY_LABEL),
                    active = activeComponent is HomeComponent.Child.TodayChild,
                )
            },
            selected = activeComponent is HomeComponent.Child.TodayChild,
            onClick = component::onTodayTabClicked,
        )

        // DailyTasks
        AppNavigationBarItem(
            modifier = Modifier.testTag("nav_tasks").weight(1f),
            icon = {
                NavigationBarText(
                    label = stringResource(loco.Res.string.MAIN_NAVIGATION_DAILY_LABEL),
                    active = activeComponent is HomeComponent.Child.DailyTasksChild,
                )
            },
            selected = activeComponent is HomeComponent.Child.DailyTasksChild,
            onClick = component::onDailyTasksTabClicked,
        )

        // Insights
        AppNavigationBarItem(
            modifier = Modifier.testTag("nav_insights").weight(1f),
            icon = {
                NavigationBarText(
                    label = stringResource(loco.Res.string.MAIN_NAVIGATION_INSIGHTS_LABEL),
                    active = activeComponent is HomeComponent.Child.InsightsChild,
                )
            },
            selected = activeComponent is HomeComponent.Child.InsightsChild,
            onClick = component::onInsightsTabClicked,
        )

        // Chat
        AppNavigationBarItem(
            modifier = Modifier.testTag("nav_chat").weight(1f),
            icon = {
                NavigationBarText(
                    label = stringResource(loco.Res.string.MAIN_NAVIGATION_CHAT_LABEL),
                    active = activeComponent is HomeComponent.Child.ChatChild,
                )
            },
            selected = activeComponent is HomeComponent.Child.ChatChild,
            onClick = {
                scope.launch {
                    globalProgressIndicator.value = true
                    component.onChatTabClicked()
                    globalProgressIndicator.value = false
                }
            },
        )

        // Profile
        AppNavigationBarItem(
            modifier = Modifier.testTag("nav_profile").weight(1f),
            icon = {
                NavigationBarText(
                    label = stringResource(loco.Res.string.MAIN_NAVIGATION_ME_LABEL),
                    active = activeComponent is HomeComponent.Child.ProfileChild,
                )
            },
            selected = activeComponent is HomeComponent.Child.ProfileChild,
            onClick = component::onProfileTabClicked,
        )
    }
}
